import React, { useState } from "react";
import Collapse from "../../../components/Collapse";

/**
 * Doli Dana bet table in the reference admin's markup — rendered inside the page's `.casino-detail`:
 *   .casino-table > .casino-table-box >
 *     .doli-main  : .players-bet (subtype "main" rows) + .side-bets (.any-pair, .odd-even-pair, .lucky7-pair)
 *     .doli-other-bets : .particular-pair (subtype "sumpair") + .sum-odds (subtype "sumtotal")
 * Markets are picked like the reference: filter `sub` by subtype, order by sr.
 * Styled by the ported reference rules in styles/index.css (+ styles/games/dolidana.css).
 */
const SEVEN_IMG = `${import.meta.env.BASE_URL}assets/img/dice/trape-seven.png`;

const normalizeString = (str) =>
  str ? String(str).replace(/\s*\(\s*/g, "(").replace(/\s*\)\s*/g, ")").replace(/\s+/g, " ").trim().toLowerCase() : "";

// Reference v-kvalpoint: >= 1000 → "{n/1000}K", else the number
const kval = (v) => {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return "";
  return n >= 1000 ? `${parseFloat((n / 1000).toFixed(2))}K` : String(parseFloat(n));
};

// Reference toCurrency (EN-IN, up to 2 decimals); 0 stays "0"
const toCurrency = (v) => (v ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : 0);

const bySubtype = (sub, subtype) =>
  sub.filter((m) => m?.subtype === subtype).sort((a, b) => (Number(a.sr) || 0) - (Number(b.sr) || 0));

export default function BetTableDoliDana({ data = [], onBetClick, exposures = {} }) {
  const sub = Array.isArray(data) ? data : [];
  const [openRange, setOpenRange] = useState({});

  const main = bySubtype(sub, "main");
  const pair = bySubtype(sub, "anypair");
  const oddeven = bySubtype(sub, "oddeven");
  const lucky7 = bySubtype(sub, "lucky7");
  const sumpair = bySubtype(sub, "sumpair");
  const sumtotal = bySubtype(sub, "sumtotal");

  const getExposure = (nat) => {
    if (!nat) return 0;
    const keys = [nat, nat.toLowerCase(), nat.trim(), nat.toLowerCase().trim(), normalizeString(nat)];
    for (const k of keys) {
      const v = parseFloat(exposures?.[k]);
      if (!Number.isNaN(v)) return v;
    }
    const norm = normalizeString(nat);
    for (const k in exposures) {
      if (normalizeString(k) === norm) {
        const v = parseFloat(exposures[k]);
        if (!Number.isNaN(v)) return v;
      }
    }
    return 0;
  };

  const isSuspended = (m) => m?.gstatus !== "OPEN";
  const click = (m) => () => {
    if (!m || isSuspended(m)) return;
    onBetClick?.(m.b, m.nat, m, "back");
  };
  const bookCls = (amt) => (amt > 0 ? "book-green" : "book-red");

  const toggle = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenRange((s) => ({ ...s, [id]: !s[id] }));
  };

  // i.fas.fa-info-circle (collapse toggle) + div.icon-range.collapse#range{sid}
  const range = (m, indent = "") => {
    const id = `range${m?.sid ?? ""}`;
    const open = !!openRange[id];
    return (
      <>
        <i
          data-toggle="collapse"
          data-target={`#${id}`}
          aria-expanded={open ? "true" : "false"}
          className={`fas fa-info-circle${open ? "" : " collapsed"}`}
          onClick={toggle(id)}
        />{" "}
        <Collapse id={id} open={!!open} className="icon-range">
          {`\n${indent}R:`}
          <span>{kval(m?.min)}</span>-<span>{kval(m?.max)}</span>
        </Collapse>
      </>
    );
  };

  // Fancy market: .odd-name (name + range) + .casino-odds-box (odds + fancy book)
  const fancyBox = (m, name, key, oddsSuffix = "") => {
    if (!m) return null;
    const amt = getExposure(m.nat);
    const susp = isSuspended(m);
    return (
      <div key={key} className="doli-odds-box">
        <div className="odd-name">
          {` ${name} `}
          <div className="float-right">{range(m)}</div>
        </div>{" "}
        <div className={`casino-odds-box back${susp ? " suspended lock-top" : ""}`} onClick={click(m)}>
          <span className="casino-odds">{`${m.b ?? ""}${oddsSuffix}`}</span>{" "}
          <span className={`casino-volume ${bookCls(amt)}`}>{toCurrency(amt)}</span>
        </div>
      </div>
    );
  };

  const any = pair[0];

  return (
    <div className="casino-table">
      <div className="casino-table-box">
        <div className="doli-main">
          <div className="players-bet">
            {main.map((m, i) => {
              const amt = getExposure(m.nat);
              return (
                <div key={m.sid ?? i} className="casino-table-row">
                  <div className="casino-nation-detail">
                    <div className="casino-nation-name">
                      {` ${m.nat} `}
                      <div className="float-right">
                        <span className={`mr-2 ${bookCls(amt)}`}>{toCurrency(amt)}</span> {range(m)}
                      </div>
                    </div>
                  </div>{" "}
                  <div className="doli-odds-box">
                    <div className={`casino-odds-box back${isSuspended(m) ? " suspended" : ""}`} onClick={click(m)}>
                      <span className="casino-odds">{m.b}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>{" "}
          <div className="side-bets">
            <div className="any-pair">{fancyBox(any, any?.nat, "any", " ")}</div>{" "}
            <div className="odd-even-pair">
              <div className="bets-box">{oddeven.map((m, i) => fancyBox(m, m.nat, m.sid ?? i))}</div>
            </div>{" "}
            <div className="lucky7-pair">
              <div className="bets-box">
                {fancyBox(lucky7[2], "< 7", "lt")}{" "}
                <div className="doli-odds-box">
                  <div className="seven-box">
                    <img src={SEVEN_IMG} />
                  </div>
                </div>{" "}
                {fancyBox(lucky7[1], "7 >", "gt")}
              </div>
            </div>
          </div>
        </div>{" "}
        <div className="doli-other-bets">
          <div className="particular-pair">
            <h4>Particular Pair</h4>{" "}
            <div className="bets-box">{sumpair.map((m, i) => fancyBox(m, m.nat, m.sid ?? i))}</div>
          </div>{" "}
          <div className="sum-odds">
            <h4>Odds of Sum Total</h4>{" "}
            <div className="bets-box">{sumtotal.map((m, i) => fancyBox(m, m.nat, m.sid ?? i))}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
